import MathUtils from '../math/MathUtils.js';

/**
 * OrbitalCameraController - Orbital camera that orbits around a target position
 *
 * Works alongside the existing Camera class without modifying it.
 * The camera position follows a target (player character), and Q/E rotates
 * the view around the target rather than rotating the camera in place.
 */
export default class OrbitalCameraController {
  /**
   * Create a new orbital camera controller
   * @param {Camera} camera - The camera to control
   * @param {Object} options - Controller options
   */
  constructor(camera, options = {}) {
    this.camera = camera;

    // Target position (player character position in world units)
    this.target = { x: 0, y: 0, z: 0 };

    // Orbital parameters
    this.orbitRadius = options.orbitRadius || 0; // Distance from target (0 = centered)
    this.targetOrbitAngle = camera.angle; // Target angle (where user wants to rotate)
    this.currentOrbitAngle = camera.angle; // Current interpolated angle

    // Zoom parameters
    this.targetZoomLevel = camera.zoomLevel; // Target zoom (where user wants to zoom)
    this.currentZoomLevel = camera.zoomLevel; // Current interpolated zoom

    // Smoothing parameters
    this.followSpeed = options.followSpeed || 8.0; // Position follow speed
    this.rotationSpeed = options.rotationSpeed || 15.0; // Rotation follow speed (higher = snappier)
    this.zoomSpeed = options.zoomSpeed || 12.0; // Zoom follow speed (higher = snappier)
    this.orbitSpeed = options.orbitSpeed || 90; // Degrees per second (input rate)

    // Current interpolated position (in camera pixel coordinates)
    this.currentPosition = { x: camera.position.x, y: camera.position.y, z: camera.position.z };

    // Mode flag
    this.enabled = false;

    // Offset from target center (for visual framing)
    this.targetOffset = { x: 0, y: 0, z: 0 };
  }

  /**
   * Enable orbital camera mode
   * @param {Object} initialTarget - Initial target position { x, y, z } in world units
   */
  enable(initialTarget = null) {
    this.enabled = true;
    if (initialTarget) {
      this.target.x = initialTarget.x;
      this.target.y = initialTarget.y;
      this.target.z = initialTarget.z;
    }
    // Sync orbit angles with current camera angle
    this.targetOrbitAngle = this.camera.angle;
    this.currentOrbitAngle = this.camera.angle;
    // Sync zoom with current camera zoom
    this.targetZoomLevel = this.camera.zoomLevel;
    this.currentZoomLevel = this.camera.zoomLevel;
    // Initialize current position to avoid jump
    this.snapToTarget();
  }

  /**
   * Disable orbital camera mode (return to free camera)
   */
  disable() {
    this.enabled = false;
  }

  /**
   * Set the target position (player character position in world units)
   * @param {number} x - World X coordinate
   * @param {number} y - World Y coordinate (height)
   * @param {number} z - World Z coordinate
   */
  setTarget(x, y, z) {
    this.target.x = x;
    this.target.y = y;
    this.target.z = z;
  }

  /**
   * Set target from a character entity
   * @param {Character} character - The character to follow
   */
  setTargetCharacter(character) {
    if (character) {
      this.setTarget(character.x, character.y, character.z);
    }
  }

  /**
   * Orbit the camera around the target
   * @param {string} direction - 'left' or 'right'
   * @param {number} deltaTime - Time since last frame
   */
  orbit(direction, deltaTime = 1 / 60) {
    if (!this.enabled) return;

    const rotateAmount = this.orbitSpeed * deltaTime;

    if (direction === 'left') {
      this.targetOrbitAngle -= rotateAmount;
    } else if (direction === 'right') {
      this.targetOrbitAngle += rotateAmount;
    }

    // Normalize target angle
    this.targetOrbitAngle = MathUtils.normalizeAngle(this.targetOrbitAngle);
  }

  /**
   * Calculate the target camera position based on target and current orbit angle
   * @returns {{ x: number, y: number, z: number }} Target camera position in pixel coordinates
   */
  calculateOrbitalPosition() {
    // Convert current orbit angle to radians
    const rad = this.currentOrbitAngle * MathUtils.DEG_TO_RAD;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Calculate offset position based on orbit radius
    // The camera is positioned "behind" the viewing direction
    // At angle 0, camera looks toward +Z, so camera is at target
    const offsetX = -sin * this.orbitRadius;
    const offsetZ = cos * this.orbitRadius;

    // Convert target world coords to camera pixel coords
    // In this engine: camera.position is in pixels
    // World coords are multiplied by CUBE_SIZE to get pixel coords
    // Z is negated for isometric coordinate system
    // Y follows the target's height for vertical camera panning
    return {
      x: this.target.x * this.camera.CUBE_SIZE + offsetX,
      y: this.target.y * this.camera.CUBE_SIZE,
      z: -this.target.z * this.camera.CUBE_SIZE + offsetZ
    };
  }

  /**
   * Update camera position and rotation with smooth interpolation
   * @param {number} deltaTime - Time since last frame
   */
  updateCameraPosition(deltaTime = 1 / 60) {
    if (!this.enabled) return;

    // Smooth rotation interpolation
    const rotationFactor = 1 - Math.exp(-this.rotationSpeed * deltaTime);

    // Calculate shortest angle difference (handles wrapping around 360)
    let angleDiff = this.targetOrbitAngle - this.currentOrbitAngle;
    // Normalize to [-180, 180] for shortest path
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;

    // Apply smooth interpolation to angle
    this.currentOrbitAngle += angleDiff * rotationFactor;
    this.currentOrbitAngle = MathUtils.normalizeAngle(this.currentOrbitAngle);

    // Update camera angle for rendering
    this.camera.angle = this.currentOrbitAngle;

    const targetPos = this.calculateOrbitalPosition();

    // Smooth position interpolation using exponential decay
    // factor = 1 - e^(-speed * dt) gives consistent feel regardless of frame rate
    const factor = 1 - Math.exp(-this.followSpeed * deltaTime);

    // Lerp current position toward target (x, y, z)
    this.currentPosition.x = MathUtils.lerp(
      this.currentPosition.x,
      targetPos.x,
      factor
    );
    this.currentPosition.y = MathUtils.lerp(
      this.currentPosition.y,
      targetPos.y,
      factor
    );
    this.currentPosition.z = MathUtils.lerp(
      this.currentPosition.z,
      targetPos.z,
      factor
    );

    // Update actual camera position
    this.camera.position.x = this.currentPosition.x;
    this.camera.position.y = this.currentPosition.y;
    this.camera.position.z = this.currentPosition.z;

    // Smooth zoom interpolation
    const zoomFactor = 1 - Math.exp(-this.zoomSpeed * deltaTime);
    this.currentZoomLevel = MathUtils.lerp(
      this.currentZoomLevel,
      this.targetZoomLevel,
      zoomFactor
    );
    this.camera.zoomLevel = this.currentZoomLevel;
  }

  /**
   * Main update called each frame
   * @param {number} deltaTime - Time since last frame
   */
  update(deltaTime = 1 / 60) {
    if (!this.enabled) return;

    this.updateCameraPosition(deltaTime);
  }

  /**
   * Immediately snap camera to target (no smoothing)
   */
  snapToTarget() {
    if (!this.enabled) return;

    // Snap angle immediately
    this.currentOrbitAngle = this.targetOrbitAngle;
    this.camera.angle = this.currentOrbitAngle;

    // Snap zoom immediately
    this.currentZoomLevel = this.targetZoomLevel;
    this.camera.zoomLevel = this.currentZoomLevel;

    const targetPos = this.calculateOrbitalPosition();
    this.currentPosition.x = targetPos.x;
    this.currentPosition.y = targetPos.y;
    this.currentPosition.z = targetPos.z;
    this.camera.position.x = targetPos.x;
    this.camera.position.y = targetPos.y;
    this.camera.position.z = targetPos.z;
  }

  /**
   * Get the current orbit angle (the interpolated value)
   * @returns {number} Current orbit angle in degrees
   */
  getOrbitAngle() {
    return this.currentOrbitAngle;
  }

  /**
   * Get the target orbit angle (where rotation is heading)
   * @returns {number} Target orbit angle in degrees
   */
  getTargetOrbitAngle() {
    return this.targetOrbitAngle;
  }

  /**
   * Set the orbit angle directly (both target and current, for instant change)
   * @param {number} angle - Angle in degrees
   * @param {boolean} instant - If true, also sets current angle immediately
   */
  setOrbitAngle(angle, instant = true) {
    this.targetOrbitAngle = MathUtils.normalizeAngle(angle);
    if (instant) {
      this.currentOrbitAngle = this.targetOrbitAngle;
    }
    if (this.enabled) {
      this.camera.angle = this.currentOrbitAngle;
    }
  }

  /**
   * Check if orbital mode is enabled
   * @returns {boolean} True if enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Set the target zoom level (for smooth zoom interpolation)
   * @param {number} zoom - Target zoom level
   */
  setTargetZoom(zoom) {
    this.targetZoomLevel = MathUtils.clamp(zoom, this.camera.MIN_ZOOM, this.camera.MAX_ZOOM);
  }

  /**
   * Get the current zoom level (interpolated value)
   * @returns {number} Current zoom level
   */
  getZoomLevel() {
    return this.currentZoomLevel;
  }

  /**
   * Get the target zoom level
   * @returns {number} Target zoom level
   */
  getTargetZoomLevel() {
    return this.targetZoomLevel;
  }
}
